import { useEffect, useMemo, useRef, useState } from "react";
import CPLTextEditor from "../../cplTextEditor/CPLTextEditor";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { ExistingFile } from "cpl-react-common/components/Inputs/fileUpload/components/FilePreview";
import { toast } from "react-toastify";
import "./Comment.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faTrash, faPen, faCircleUser } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate } from "react-router";
import { CommentUpdateModel } from "services/classes/CommentUpdateModel";
import { NewCommentModel } from "services/classes/NewCommentModel";
import { CommentModel } from "services/api/cplapi/CPLAPI";
import Autosave from "../../autosave/Autosave";
import { CPLFileUpload } from "../../cplFileUpload/CPLFileUpload";
import { sanitizeHtml } from "cpl-react-common/Helpers/HtmlSanitizerHelpers";

type ExistingCommentProps = {
  comment: CommentModel;
  currentUserID: string | undefined;
  newComment: false;
  externalAccess: false;
  handleSave: (comment: CommentUpdateModel) => void;
  downloadAttachment: (attachmentID: number) => Promise<File>;
  handleMakeCommentPublic: () => void;
  handleDeleteComment: () => void;
  handleMarkAsRead: (commentID: number) => void;
  loading: boolean;
  autosavedValues?: never;
  autosaveFormKey?: never;
};

type NewCommentProps = {
  comment?: never;
  currentUserID?: never;
  newComment: true;
  externalAccess: false;
  handleSave: (comment: NewCommentModel) => void;
  downloadAttachment?: never;
  handleMakeCommentPublic?: never;
  handleDeleteComment?: never;
  handleMarkAsRead?: never;
  loading: boolean;
  autosavedValues: NewCommentModel;
  autosaveFormKey: string;
};

type ExternalAccessCommentProps = {
  comment?: never;
  currentUserID?: never;
  newComment: true;
  externalAccess: true;
  handleSave: (comment: NewCommentModel) => void;
  downloadAttachment?: never;
  handleMakeCommentPublic?: never;
  handleDeleteComment?: never;
  handleMarkAsRead?: never;
  loading: boolean;
  autosavedValues?: never;
  autosaveFormKey?: never;
};

type CommentProps = NewCommentProps | ExistingCommentProps | ExternalAccessCommentProps;

enum CommentTabs {
  Message,
  Attachments,
}

type LinkedRecord = {
  linkType: "Task" | "Case" | "Job";
  linkUrl: string;
};

const validationSchema = Yup.object().shape({
  message: Yup.string().required("Comment message is required"),
  private: Yup.bool().required(),
});

const MAX_IMAGE_SIZE_IN_BYTES = 1024 * 1024 * 20; //20MB

const Comment: React.FC<CommentProps> = ({
  comment,
  handleSave,
  downloadAttachment,
  handleMakeCommentPublic,
  handleDeleteComment,
  handleMarkAsRead,
  currentUserID,
  newComment,
  loading,
  autosavedValues,
  autosaveFormKey,
  externalAccess,
}: CommentProps) => {
  const [editing, setEditing] = useState(newComment);
  const [activeTab, setActiveTab] = useState<CommentTabs>(CommentTabs.Message);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<ExistingFile[]>([]);
  const navigate = useNavigate();
  const allowEditing = newComment || (currentUserID !== undefined && currentUserID === comment.userID);
  const numAttachments = (comment?.existingAttachments?.length ?? 0) + newFiles.length - deletedFiles.length;

  const commentRef = useRef<HTMLDivElement>(null);
  const markedRead = useRef(false);

  const [isUnread, setIsUnread] = useState(!newComment && comment?.isUnread);

  useEffect(() => {
    if (newComment || externalAccess || !comment?.commentID || markedRead.current) return;

    const element = commentRef.current;
    if (!element) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !markedRead.current) {
            timeoutId = setTimeout(() => {
              if (!markedRead.current) {
                markedRead.current = true;
                setIsUnread(false);
                handleMarkAsRead(comment.commentID!);
              }
            }, 2000);
          } else if (!entry.isIntersecting && timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "0px",
      }
    );

    observer.observe(element);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.unobserve(element);
    };
  }, [newComment, externalAccess, comment?.commentID, handleMarkAsRead]);

  const initialFiles = useMemo(() => {
    return !newComment && comment.existingAttachments !== undefined
      ? comment.existingAttachments.map((x) => ({
          id: x.commentAttachmentID ?? "",
          name: x.fileName ?? "",
          size: x.fileSize ?? 0,
          previewURL: x.sasUri ?? undefined,
        }))
      : [];
  }, [comment?.existingAttachments, newComment]);

  const linkedRecord = useMemo<LinkedRecord | undefined>(() => {
    if (!newComment) {
      if (comment.userTaskID !== undefined && comment.userTaskID !== null) {
        return {
          linkType: "Task",
          linkUrl: `/UserTask/${comment.userTaskID}`,
        };
      } else if (comment.caseID !== undefined && comment.caseID !== null) {
        return {
          linkType: "Case",
          linkUrl: `/Case/${comment.caseID}`,
        };
      } else if (
        comment.jobID !== undefined &&
        comment.jobID !== null &&
        comment.taskNo !== undefined &&
        comment.taskNo !== null
      ) {
        return {
          linkType: "Job",
          linkUrl: "", // TODO: Implement link to jobs when cpl2 linking is added
        };
      }
    }
    return undefined;
  }, [newComment, comment]);

  return (
    <div ref={commentRef} className={isUnread ? "fw-bold" : ""}>
      <Formik
        validationSchema={validationSchema}
        initialValues={
          comment === undefined
            ? externalAccess
              ? new NewCommentModel({ message: "", private: false })
              : autosavedValues
            : comment
        }
        enableReinitialize
        onSubmit={(values, { resetForm }) => {
          handleSave(
            newComment
              ? new NewCommentModel({
                  message: values.message,
                  private: values.private,
                  attachments: newFiles.map((file) => ({ data: file, fileName: file.name })),
                })
              : new CommentUpdateModel({
                  ...comment,
                  message: values.message,
                  private: values.private,
                  deletedAttachmentIDs: deletedFiles.map((x) => +x.id),
                  newAttachments: newFiles.map((file) => ({ data: file, fileName: file.name })),
                })
          );
          setEditing(false);
          resetForm();
          setNewFiles([]);
          setDeletedFiles([]);
          setActiveTab(CommentTabs.Message);
        }}
      >
        {({ values, setFieldValue, resetForm }) => (
          <Form>
            {newComment && !externalAccess && <Autosave formKey={autosaveFormKey!} />}
            {/* Comment header */}
            <div className="d-flex overflow-auto align-items-center">
              {!newComment && (
                <div className="ms-auto ms-sm-0">
                  <div className="d-flex gap-4 align-items-center text-primary">
                    {comment.private && (
                      <div
                        data-test-id={`comment-form__make-public-button-${comment.commentID}`}
                        className="badge badge-light-warning cursor-pointer"
                        onClick={() => handleMakeCommentPublic()}
                      >
                        Private
                      </div>
                    )}
                    <FontAwesomeIcon icon={faCircleUser} size="lg" />
                    <div className="fw-bold">{comment.systemComment ? "System" : comment.commentCreatorName}</div>
                    <div>{comment.commentDate!.toCPLCommentDateTimeString()}</div>
                    <ul className="nav fw-bold border-transparent flex-nowrap p-0" role="tablist">
                      <li className="nav-item">
                        <div
                          data-test-id={`comment-form__attachments-tab-${comment.commentID}`}
                          className={`nav-link p-0 cursor-pointer ${
                            activeTab === CommentTabs.Attachments ? "active" : "text-muted"
                          } ${editing || numAttachments > 0 ? "" : "d-none"}`}
                          onClick={() => {
                            setActiveTab(
                              activeTab === CommentTabs.Attachments ? CommentTabs.Message : CommentTabs.Attachments
                            );
                          }}
                          role="tab"
                        >
                          <FontAwesomeIcon icon={faPaperclip} className="p-0" /> <span>({numAttachments})</span>
                        </div>
                      </li>
                    </ul>
                    {!newComment && (
                      <>
                        {linkedRecord !== undefined && (
                          <button
                            data-test-id={`comment-form__view-linked-record-button-${comment.commentID}`}
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(linkedRecord.linkUrl)}
                          >
                            View {linkedRecord.linkType}
                          </button>
                        )}
                        {allowEditing && (
                          <div className="ms-auto d-flex gap-4 text-dark">
                            {!editing && comment.private && (
                              <FontAwesomeIcon
                                data-test-id={`comment-form__edit-button-${comment.commentID}`}
                                icon={faPen}
                                className="cursor-pointer"
                                onClick={() => setEditing(true)}
                              />
                            )}
                            <FontAwesomeIcon
                              data-test-id={`comment-form__delete-button-${comment.commentID}`}
                              icon={faTrash}
                              className="cursor-pointer"
                              onClick={handleDeleteComment}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Comment body */}
            <div className="mt-1">
              <div className="tab-content">
                <div className={`tab-pane comment-message ${activeTab === CommentTabs.Message ? "active" : ""}`}>
                  <>
                    {newComment || editing ? (
                      <div className="mt-4">
                        <CPLTextEditor
                          data-test-id={`comment-form__message-text-editor${
                            comment?.commentID ? `-${comment.commentID}` : ""
                          }`}
                          value={values.message}
                          onChange={(value) => {
                            setFieldValue("message", value);
                          }}
                          disabled={loading}
                          height={200}
                          allowImages={!externalAccess}
                          enableAutoComplete={!externalAccess}
                          maxImageSizeBytes={MAX_IMAGE_SIZE_IN_BYTES}
                        />
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">
                            <ErrorMessage name="message" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.message) }} />
                    )}
                  </>
                </div>
                <div
                  className={`tab-pane attachment-uploader ${activeTab === CommentTabs.Attachments ? "active" : ""}`}
                >
                  <div className="mt-4">
                    <CPLFileUpload
                      data-test-id={`comment-form__attachments-file-upload${
                        comment?.commentID ? `-${comment.commentID}` : ""
                      }`}
                      initialFiles={initialFiles}
                      disabled={!newComment && !editing}
                      updateNewFiles={(files) => {
                        setNewFiles(files);
                      }}
                      updateDeletedFiles={(files) => {
                        setDeletedFiles(deletedFiles.concat(files));
                      }}
                      downloadFile={
                        downloadAttachment !== undefined ? (file) => downloadAttachment(+file.id) : undefined
                      }
                      displayError={(error) => toast.warn(error)}
                      loading={loading}
                      emptyUploaderHeightPx={100}
                      key={editing.toString()}
                    />
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="message" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Comment footer */}
            <div className="mt-3">
              <div className="d-inline-flex">
                <div className="d-flex flex-wrap align-items-center gap-4">
                  {newComment || editing ? (
                    <>
                      <div className="ms-auto">
                        <button
                          data-test-id={`comment-form__submit-button${
                            comment?.commentID ? `-${comment.commentID}` : ""
                          }`}
                          className="btn btn-success"
                          type="submit"
                        >
                          {newComment ? "Post Comment" : "Save Changes"}
                        </button>
                      </div>
                      {!externalAccess && (newComment || comment.private) && (
                        <div className="d-flex ps-2 py-1 ms-auto">
                          <label className="form-label pe-2 mb-0">Private</label>
                          <Field
                            data-test-id={`comment-form__private-checkbox${
                              comment?.commentID ? `-${comment.commentID}` : ""
                            }`}
                            className="form-check-input"
                            type="checkbox"
                            name="private"
                          />
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              <ErrorMessage name="private" />
                            </div>
                          </div>
                        </div>
                      )}
                      {!newComment && (
                        <div className="ps-2 py-1 ms-auto">
                          <button
                            data-test-id={`comment-form__cancel-button-${comment.commentID}`}
                            className="btn btn-danger"
                            type="button"
                            onClick={() => {
                              setEditing(false);
                              setNewFiles([]);
                              setDeletedFiles([]);
                              resetForm();
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Comment;
